import React, { useState } from "react";
import {
  Alert,
  Box,
  Dialog,
  IconButton,
  Link,
  Typography,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { GearDesign } from "../types";

export default function GearDesignPicker({
  designs,
  selectedId,
  onSelect,
  label,
  sizeChartUrl,
}: {
  designs: GearDesign[];
  selectedId?: string;
  onSelect: (id: string) => void;
  label: string;
  sizeChartUrl?: string;
}) {
  const [sizeChartOpen, setSizeChartOpen] = useState(false);

  const sizeChartLink = sizeChartUrl ? (
    <>
      <Link
        component="button"
        type="button"
        variant="caption"
        onClick={() => setSizeChartOpen(true)}
        sx={{ ml: 1, fontWeight: 600 }}
      >
        📏 View Size Chart
      </Link>
      <Dialog
        open={sizeChartOpen}
        onClose={() => setSizeChartOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <IconButton
          onClick={() => setSizeChartOpen(false)}
          sx={{ position: "absolute", top: 4, right: 4, bgcolor: "background.paper" }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          component="img"
          src={sizeChartUrl}
          alt="Size chart"
          sx={{ width: "100%", height: "auto", display: "block" }}
        />
      </Dialog>
    </>
  ) : null;

  if (designs.length === 0) {
    return (
      <Alert severity="warning" sx={{ mb: 1 }}>
        No {label.toLowerCase()} designs available yet
      </Alert>
    );
  }
  return (
    <Box sx={{ mb: 1 }}>
      <Typography
        variant="caption"
        color={selectedId ? "text.secondary" : "error"}
        sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
      >
        {label} *{sizeChartLink}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 1.5,
        }}
      >
        {designs.map((design) => {
          const isSelected = design.id === selectedId;
          return (
            <Box
              key={design.id}
              onClick={() => onSelect(design.id)}
              sx={{
                cursor: "pointer",
                border: "2px solid",
                borderColor: isSelected ? "primary.main" : "divider",
                borderRadius: 2,
                overflow: "hidden",
                position: "relative",
                bgcolor: "background.paper",
                boxShadow: isSelected ? 3 : 0,
              }}
            >
              {isSelected && (
                <CheckCircleIcon
                  color="primary"
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    bgcolor: "white",
                    borderRadius: "50%",
                    fontSize: 28,
                  }}
                />
              )}
              <Box
                sx={{
                  width: "100%",
                  bgcolor: "action.hover",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 100,
                }}
              >
                {design.imageUrl ? (
                  <Box
                    component="img"
                    src={design.imageUrl}
                    alt={design.name}
                    sx={{
                      width: "100%",
                      height: "auto",
                      maxHeight: 280,
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                ) : (
                  <Typography variant="h2" sx={{ py: 4 }}>
                    🎽
                  </Typography>
                )}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  display: "block",
                  textAlign: "center",
                  px: 1,
                  py: 0.75,
                  fontWeight: isSelected ? 700 : 500,
                }}
              >
                {design.name}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
